import { useEffect, useRef, useState, type RefObject } from "react";

export type CameraFacing = "user" | "environment";

export type QrCameraStatus =
  | "starting"
  | "scanning"
  | "blocked"
  | "unavailable"
  | "stopped";

/**
 * Continuous camera QR decoding for the entrance kiosk (#2691), using ZXing
 * (`@zxing/browser`, pure JavaScript, works on Safari/iPadOS where the native
 * BarcodeDetector does not exist). The library is loaded only when the kiosk
 * camera starts, so player screens never download it.
 *
 * Every decoded frame is passed to `onDecode`; the caller's scan gate decides
 * what counts. The decoded text is never rendered.
 */
export function useQrCamera({
  videoRef,
  active,
  facing,
  onDecode,
  restartKey,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  active: boolean;
  facing: CameraFacing;
  onDecode: (text: string) => void;
  /** Change it to restart the camera on a newly mounted video element. */
  restartKey?: string;
}): QrCameraStatus {
  const [status, setStatus] = useState<QrCameraStatus>("stopped");
  const onDecodeRef = useRef(onDecode);
  useEffect(() => {
    onDecodeRef.current = onDecode;
  }, [onDecode]);

  useEffect(() => {
    if (!active) {
      setStatus("stopped");
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("unavailable");
      return;
    }
    let cancelled = false;
    let stop: (() => void) | null = null;
    setStatus("starting");

    void (async () => {
      try {
        const { BrowserQRCodeReader } = await import("@zxing/browser");
        if (cancelled) return;
        const reader = new BrowserQRCodeReader(undefined, {
          delayBetweenScanAttempts: 120,
          delayBetweenScanSuccess: 300,
        });
        const controls = await reader.decodeFromConstraints(
          { audio: false, video: { facingMode: { ideal: facing } } },
          video,
          (result) => {
            if (result && !cancelled) onDecodeRef.current(result.getText());
          },
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        stop = () => controls.stop();
        setStatus("scanning");
      } catch (error) {
        if (cancelled) return;
        const name = (error as { name?: string } | null)?.name;
        setStatus(
          name === "NotAllowedError" || name === "SecurityError" ? "blocked" : "unavailable",
        );
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [active, facing, videoRef, restartKey]);

  return status;
}
